import {
  AdLoadInfo,
  AdMob,
  AdMobBannerSize,
  AdMobRewardItem,
  AdOptions,
  InterstitialAdPluginEvents,
  BannerAdOptions,
  BannerAdPluginEvents,
  RewardAdOptions,
  RewardAdPluginEvents,
} from '@capacitor-community/admob';
import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { PluginListenerHandle } from '@capacitor/core';
import { Platform } from '@ionic/angular';
import {
  BehaviorSubject,
  Subscription,
  combineLatest,
  filter,
  firstValueFrom,
  map,
  startWith,
  take,
} from 'rxjs';
import {
  bannerBottomOptions,
  insideAppTravelingOptions,
  insideIosAppTravelingOptions,
  leavingAppInterstitialOptions,
  shellConvertingOptions,
  treasureOpeningOptions,
} from './ad.options';
import { PaymentsStore } from 'src/app/payments/payments-store';
import { PaymentsService } from 'src/app/payments/payments.service';
import { UserService } from 'src/app/auth/user.service';
import { environment } from 'src/environments/environment';
import { AppService } from 'src/app/app.service';
import { getMobileFooterBannerRouteDecision } from './mobile-footer-banner-route-policy';
import mixpanel from 'mixpanel-browser';

type BannerPlacement = 'bottom' | 'traveling';
type BottomBannerState = 'none' | 'showing' | 'visible' | 'hidden' | 'removed';
type RewardAttemptOutcome = 'rewarded' | 'shown' | 'failed';
type SanitizedAdError = {
  code?: unknown;
  domain?: unknown;
  name?: unknown;
  message?: unknown;
  responseInfo?: unknown;
  status?: unknown;
};
export type TreasureAdOutcome = 'rewarded' | 'interstitial-fallback' | 'no-ad-fallback';

@Injectable({
  providedIn: 'root',
})
export class AdmobService {
  constructor(
    private platform: Platform,
    private location: Location,
    private paymentsStore: PaymentsStore,
    private paymentService: PaymentsService,
    private userService: UserService,
    private router?: Router,
    private appService?: AppService
  ) {}

  appMargin = 0;
  bannerLoaded = false;

  isMobile = this.platform.platforms().includes('capacitor');
  isMobileWeb = this.platform.platforms().includes('mobileweb');
  isIosApp = this.platform.platforms().includes('ios') && this.isMobile;

  resizedAd = new BehaviorSubject<boolean>(false);
  isPremium = false;
  isAdmin = false;
  isRewarded = new BehaviorSubject<boolean>(false);

  private readonly listenerHandlers: PluginListenerHandle[] = [];
  private readonly enableProductionAdDiagnostics = false;
  private readonly bottomBannerClass = 'admob-bottom-banner-visible';
  private readonly keyboardVisibleClass = 'admob-keyboard-visible';
  private readonly bottomBannerHeightVar = '--mermapp-admob-banner-height';
  private activeBannerPlacement: BannerPlacement | null = null;
  private currentBannerAdId: string | null = null;
  private listenersRegistered = false;
  private bottomBannerState: BottomBannerState = 'none';
  private bannerShowWatchdogTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly bannerShowWatchdogMs = 10000;
  private footerBannerControllerSubscription: Subscription | null = null;
  private footerBannerDecisionQueue: Promise<void> = Promise.resolve();
  private footerBannerCooldownUntil: number | null = null;
  private footerBannerLastFailureReason: SanitizedAdError | null = null;
  private lastTravelingBannerRemovalAt: number | null = null;
  private postTravelingFooterRestoreTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly footerBannerCooldownMs = 60000;
  private readonly postTravelingFooterRestoreDelayMs = 3000;
  private static footerBannerModalListenersRegistered = false;
  private static footerBannerKeyboardListenersRegistered = false;
  private static activeFooterBannerController: AdmobService | null = null;
  private adMob = AdMob;
  private keyboardBlurTimer: ReturnType<typeof setTimeout> | null = null;
  private footerBannerControllerContext: {
    source: string;
    url: string;
    hasUser: boolean;
    adGateReady: boolean;
    isTravelingOpen: boolean;
  } | null = null;
  private readonly footerBannerSuppressionSources = new Set<string>();
  private readonly suppressingModalComponentSelectors = [
    'app-add-events',
    'app-edit-events',
    'app-add-pod',
    'app-edit-pod',
    'app-add-pool',
    'app-edit-pool',
    'app-add-second-hand-item',
    'app-edit-second-hand-item',
    'app-add-job',
    'app-edit-job',
    'app-add-service',
    'app-edit-service',
    'app-edit-profile',
    'app-add-planet-item',
    'app-edit-planet-item',
    'app-add-experience',
    'app-add-gallery-item',
    'app-add-channel',
    'app-add-blog-post',
    'app-add-participants',
    'app-question-modal',
  ];
  private readonly suppressingModalClassTokens = [
    'add-modal',
    'form-modal',
    'edit-events-modal',
    'add-event-modal',
    'edit-pool-modal',
    'add-pod-modal',
    'edit-pod-modal',
    'add-second-hand-modal',
    'edit-second-hand__modal',
    'add-service_modal',
    'edit-service_modal',
    'form-modal_style',
    'add-gallery-modal',
    'add-experience-modal',
    'edit-planet-modal',
    'pod-question-form-modal',
  ];
  private readonly nonSuppressingModalClassTokens = [
    'swiper-modal',
    'swiper_modal',
    'image-viewer-modal',
    'discord-modal',
    'discord_modal',
    'premium-modal',
    'pending-modal',
    'confirm-modal',
    'flag-modal',
  ];

  async showBottomBanner(source = 'unknown') {
    await this.showBanner(bannerBottomOptions, 'bottom', source);
  }

  startMobileFooterBannerController(source = 'app-start') {
    if (this.footerBannerControllerSubscription) {
      this.logBanner('route-controller-start-skipped', { source });
      return;
    }

    if (!this.router || !this.appService) {
      this.logBanner('route-controller-start-skipped-missing-deps', { source });
      return;
    }

    this.registerFooterBannerModalListeners();
    this.registerFooterBannerKeyboardListeners();

    const route$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects || event.url),
      startWith(this.router.url || this.location.path() || '/')
    );

    this.footerBannerControllerSubscription = combineLatest([
      route$,
      this.userService.currentUser$,
      this.appService.showTravelingModal,
      this.getAdGateReadyStream(),
    ]).subscribe(([url, user, isTravelingOpen, adGateReady]) => {
      this.footerBannerDecisionQueue = this.footerBannerDecisionQueue
        .then(() =>
          this.applyMobileFooterBannerRouteDecision({
            source,
            url,
            hasUser: !!user?.uid,
            adGateReady,
            isTravelingOpen,
          })
        )
        .catch(error => {
          this.logBanner('route-controller-decision-failed', {
            source,
            url,
            error,
          });
        });
    });

    this.logBanner('route-controller-started', { source });
  }

  setFooterBannerSuppressed(source: string, suppressed: boolean) {
    const normalizedSource = source || 'unknown';
    const hadSource = this.footerBannerSuppressionSources.has(normalizedSource);
    if (hadSource === suppressed) {
      this.logBanner('footer-suppression-unchanged', {
        source: normalizedSource,
        suppressed,
      });
      return;
    }

    const wasSuppressed = this.footerBannerSuppressionSources.size > 0;

    if (suppressed) {
      this.footerBannerSuppressionSources.add(normalizedSource);
    } else {
      this.footerBannerSuppressionSources.delete(normalizedSource);
    }

    const isSuppressed = this.footerBannerSuppressionSources.size > 0;
    this.logBanner('footer-suppression-changed', {
      source: normalizedSource,
      suppressed,
      activeSuppressionSources: Array.from(this.footerBannerSuppressionSources),
    });

    if (wasSuppressed === isSuppressed) {
      this.logBanner('footer-suppression-state-unchanged', {
        source: normalizedSource,
        suppressed,
      });
      return;
    }

    this.queueCurrentFooterBannerDecision(`suppression:${normalizedSource}`);
  }

  async showTravelingAd(source = 'traveling') {
    if (this.activeBannerPlacement === 'bottom' || this.bottomBannerState === 'hidden') {
      this.lastTravelingBannerRemovalAt = Date.now();
      this.logBanner('traveling-clear-bottom-before-mrec', { source });
      await this.removeBanner(`${source}:clear-bottom-before-traveling`);
    }

    if (this.isIosApp) {
      await this.showBanner(insideIosAppTravelingOptions, 'traveling', source);
    } else {
      await this.showBanner(insideAppTravelingOptions, 'traveling', source);
    }
  }

  async showMediaAdv(source = 'media') {
    this.logBanner('show-media-requested', { source });
    await this.ionViewWillEnter();
    if (
      this.location.path().includes('bazaar/') ||
      this.location.path().includes('media')
    ) {
      await this.showBottomBanner(source);
    } else {
      this.logBanner('show-media-skipped-path', { source });
    }
  }

  async showLeavingAd() {
    await this.showInterstitialAd(leavingAppInterstitialOptions, 'leaving-app');
  }

  async showTreasureOpeningAd(): Promise<TreasureAdOutcome> {
    const rewardOutcome = await this.showRewardAd(
      treasureOpeningOptions,
      'treasure-opening'
    );

    if (rewardOutcome === 'rewarded' || rewardOutcome === 'shown') {
      return 'rewarded';
    }

    const didShowInterstitial = await this.showInterstitialAd(
      leavingAppInterstitialOptions,
      'treasure-interstitial-fallback'
    );

    return didShowInterstitial ? 'interstitial-fallback' : 'no-ad-fallback';
  }

  async showShellConvertingAd() {
    await this.showRewardAd(shellConvertingOptions, 'shell-converting');
  }

  async ionViewWillEnter() {
    await this.refreshPremiumState();
    this.logBanner('will-enter');
    if (this.isMobile && !this.isPremium && !this.isAdmin) {
      this.registerListeners();
    }
  }

  async ionViewWillLeave() {
    this.syncPremiumState();
    this.logBanner('will-leave');
    if (this.isMobile) {
      this.removeListeners();
      await this.removeBanner();
      const app: HTMLElement = document.querySelector('ion-router-outlet');
      this.resetRouterOutletMargin(app);
      this.isRewarded.next(false);
    }
  }

  private async applyMobileFooterBannerRouteDecision({
    source,
    url,
    hasUser,
    adGateReady,
    isTravelingOpen,
  }: {
    source: string;
    url: string;
    hasUser: boolean;
    adGateReady: boolean;
    isTravelingOpen: boolean;
  }) {
    this.footerBannerControllerContext = {
      source,
      url,
      hasUser,
      adGateReady,
      isTravelingOpen,
    };
    this.syncPremiumState();
    const routeDecision = getMobileFooterBannerRouteDecision(url);
    const isSuppressed = this.footerBannerSuppressionSources.size > 0;
    this.logBanner('route-controller-decision', {
      source,
      url,
      routeEligible: routeDecision.eligible,
      routeReason: routeDecision.reason,
      hasUser,
      adGateReady,
      isTravelingOpen,
      isSuppressed,
      activeSuppressionSources: Array.from(this.footerBannerSuppressionSources),
    });

    if (!hasUser || this.isPremium || this.isAdmin) {
      this.clearPostTravelingFooterRestoreTimer();
      this.logBanner('route-controller-action', {
        source,
        url,
        action: 'remove-gated',
        hasUser,
        gateReason: this.adGateReason(hasUser),
      });
      await this.removeBanner(`route-controller:${routeDecision.reason}`);
      return;
    }

    if (!adGateReady) {
      this.clearPostTravelingFooterRestoreTimer();
      this.logBanner('route-controller-action', {
        source,
        url,
        action: 'remove-gated',
        hasUser,
        adGateReady,
        gateReason: this.adGateReason(hasUser, adGateReady),
      });
      await this.removeBanner(`route-controller:ad-gate-waiting`);
      return;
    }

    if (isTravelingOpen) {
      this.clearPostTravelingFooterRestoreTimer();
      this.logBanner('route-controller-action', { source, url, action: 'remove-traveling' });
      if (this.activeBannerPlacement === 'bottom' || this.bottomBannerState === 'hidden') {
        this.lastTravelingBannerRemovalAt = Date.now();
      }
      await this.removeBanner('route-controller:traveling-open');
      return;
    }

    if (isSuppressed) {
      this.logBanner('route-controller-action', { source, url, action: 'hide-suppressed' });
      await this.hideBanner('route-controller:suppressed');
      return;
    }

    if (!routeDecision.eligible) {
      this.clearPostTravelingFooterRestoreTimer();
      this.logBanner('route-controller-action', {
        source,
        url,
        action: 'hide-excluded',
        routeReason: routeDecision.reason,
      });
      await this.hideBanner(`route-controller:${routeDecision.reason}`);
      return;
    }

    this.logBanner('route-controller-action', { source, url, action: 'show-or-resume' });
    await this.ionViewWillEnter();
    await this.showBottomBanner(`route-controller:${url}`);
  }

  private queueCurrentFooterBannerDecision(source: string) {
    const context =
      this.footerBannerControllerContext ||
      (this.router
        ? {
            source,
            url: this.router.url || this.location.path() || '/',
            hasUser: !!this.userService.currentUser$.value?.uid,
            adGateReady: this.isAdGateReady(),
            isTravelingOpen: !!this.appService?.showTravelingModal?.value,
          }
        : null);

    if (!context) {
      this.logBanner('route-controller-reevaluate-skipped', { source });
      return;
    }

    this.footerBannerDecisionQueue = this.footerBannerDecisionQueue
      .then(() =>
        this.applyMobileFooterBannerRouteDecision({
          ...context,
          source,
        })
      )
      .catch(error => {
        this.logBanner('route-controller-reevaluate-failed', {
          source,
          error,
        });
        });
  }

  private registerFooterBannerModalListeners() {
    AdmobService.activeFooterBannerController = this;

    if (AdmobService.footerBannerModalListenersRegistered || typeof document === 'undefined') {
      return;
    }

    document.addEventListener('ionModalDidPresent', event => {
      const service = AdmobService.activeFooterBannerController;
      const modal = service?.getModalElementFromEvent(event);
      if (!service || !modal || !service.isFooterBannerSuppressingModal(modal)) {
        service?.logBanner('modal-presented-no-footer-suppression', {
          modal: service?.describeModal(modal) || 'unknown',
        });
        return;
      }

      service.setFooterBannerSuppressed(
        `modal:${service.describeModal(modal)}`,
        true
      );
    });

    document.addEventListener('ionModalDidDismiss', event => {
      const service = AdmobService.activeFooterBannerController;
      const modal = service?.getModalElementFromEvent(event);
      if (!service || !modal || !service.isFooterBannerSuppressingModal(modal)) {
        return;
      }

      service.setFooterBannerSuppressed(
        `modal:${service.describeModal(modal)}`,
        false
      );
    });

    AdmobService.footerBannerModalListenersRegistered = true;
    this.logBanner('modal-listeners-registered');
  }

  private registerFooterBannerKeyboardListeners() {
    AdmobService.activeFooterBannerController = this;

    if (AdmobService.footerBannerKeyboardListenersRegistered || typeof document === 'undefined') {
      return;
    }

    document.addEventListener(
      'focusin',
      event => {
        const service = AdmobService.activeFooterBannerController;
        if (!service || !service.isKeyboardFocusTarget(event.target)) {
          return;
        }

        service.setKeyboardFooterBannerSuppressed(true);
      },
      true
    );

    document.addEventListener(
      'focusout',
      event => {
        const service = AdmobService.activeFooterBannerController;
        if (!service || !service.isKeyboardFocusTarget(event.target)) {
          return;
        }

        service.setKeyboardFooterBannerSuppressed(false);
      },
      true
    );

    AdmobService.footerBannerKeyboardListenersRegistered = true;
    this.logBanner('keyboard-listeners-registered');
  }

  private setKeyboardFooterBannerSuppressed(suppressed: boolean) {
    if (this.keyboardBlurTimer) {
      clearTimeout(this.keyboardBlurTimer);
      this.keyboardBlurTimer = null;
    }

    if (suppressed) {
      document.body.classList.add(this.keyboardVisibleClass);
      this.logBanner('keyboard-suppression-on');
      this.setFooterBannerSuppressed('keyboard', true);
      return;
    }

    this.keyboardBlurTimer = setTimeout(() => {
      document.body.classList.remove(this.keyboardVisibleClass);
      this.logBanner('keyboard-suppression-off');
      this.setFooterBannerSuppressed('keyboard', false);
    }, 150);
  }

  private isKeyboardFocusTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    const tagName = target.tagName.toLowerCase();
    return (
      tagName === 'input' ||
      tagName === 'textarea' ||
      tagName === 'ion-input' ||
      tagName === 'ion-textarea' ||
      target.isContentEditable ||
      !!target.closest('ion-input, ion-textarea, [contenteditable="true"]')
    );
  }

  private getModalElementFromEvent(event: Event): HTMLElement | null {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const target = path.find(
      item => item instanceof HTMLElement && item.tagName?.toLowerCase() === 'ion-modal'
    );
    if (target instanceof HTMLElement) {
      return target;
    }

    return event.target instanceof HTMLElement ? event.target : null;
  }

  private isFooterBannerSuppressingModal(modal: HTMLElement): boolean {
    const className = modal.className?.toString().toLowerCase() || '';
    const hasFormComponent = this.suppressingModalComponentSelectors.some(selector =>
      modal.querySelector(selector)
    );
    const hasFormClass = this.suppressingModalClassTokens.some(token =>
      className.includes(token)
    );

    if (hasFormComponent || hasFormClass) {
      return true;
    }

    if (this.nonSuppressingModalClassTokens.some(token => className.includes(token))) {
      return false;
    }

    return false;
  }

  private describeModal(modal: HTMLElement | null): string {
    if (!modal) {
      return 'unknown';
    }

    return [
      modal.tagName?.toLowerCase(),
      modal.id ? `#${modal.id}` : '',
      modal.className ? `.${modal.className.toString().trim().replace(/\s+/g, '.')}` : '',
    ]
      .filter(Boolean)
      .join('');
  }

  async removeBanner(source = 'unknown') {
    this.logBanner('remove-requested', { source });
    if (this.isMobile) {
      await this.adMob.removeBanner().catch(e => {
        this.logBanner('remove-failed', { source, error: e });
        console.log(e);
      });
      this.clearActiveBanner('removed');
    } else {
      this.clearActiveBanner('removed');
    }
  }

  async hideBanner(source = 'unknown') {
    this.logBanner('hide-requested', { source });
    if (this.isMobile) {
      await this.hideActiveBanner(source);
    } else {
      this.markBottomBannerHidden();
    }
  }

  private async hideActiveBanner(source = 'unknown') {
    if (this.isMobile) {
      this.resizedAd.next(false);
      await this.adMob.hideBanner().catch(e => {
        this.logBanner('hide-failed', { source, error: e });
        console.log(e);
      });
      const app: HTMLElement = document.querySelector('ion-router-outlet');
      this.resetRouterOutletMargin(app);
      if (this.activeBannerPlacement === 'bottom') {
        this.markBottomBannerHidden();
      } else {
        this.clearActiveBanner('none');
      }
    }
  }

  async resumeBanner(source = 'unknown') {
    await this.refreshPremiumState();
    this.logBanner('resume-requested', { source });
    if (this.isMobile && !this.isPremium && !this.isAdmin) {
      await this.adMob.resumeBanner().catch(e => {
        this.logBanner('resume-failed', { source, error: e });
        console.log(e);
        this.bottomBannerState = 'removed';
      });
      if (this.bottomBannerState !== 'removed') {
        this.bottomBannerState = 'visible';
        this.activeBannerPlacement = 'bottom';
      }
    } else {
      this.logBanner('resume-skipped-gate', { source });
    }
  }

  private async showBanner(
    options: BannerAdOptions,
    placement: BannerPlacement,
    source = 'unknown'
  ): Promise<void> {
    await this.refreshPremiumState();
    this.logBanner('show-requested', {
      source,
      adId: options.adId,
      adSize: options.adSize,
      position: options.position,
      isTesting: options.isTesting,
      placement,
      adGateReady: this.isAdGateReady(),
    });

    if (!this.isMobile) {
      this.logBanner('show-would-request-native-banner', {
        source,
        placement,
        adId: options.adId,
      });
      return;
    }

    if (!this.isAdGateReady()) {
      this.logBanner('show-skipped-gate', {
        source,
        placement,
        gateReason: this.adGateReason(true, false),
      });
      return;
    }

    if (this.isPremium || this.isAdmin) {
      this.logBanner('show-skipped-gate', {
        source,
        placement,
        gateReason: this.adGateReason(true, this.isAdGateReady()),
      });
      return;
    }

    if (this.isMobile && !this.isPremium && !this.isAdmin) {
      this.registerListeners();

      if (placement === 'bottom' && this.shouldSkipFooterRequestForCooldown(options.adId, source)) {
        return;
      }

      if (placement === 'bottom' && this.shouldSkipFooterRequestAfterTraveling(source)) {
        return;
      }

      if (
        placement === 'bottom' &&
        this.bottomBannerState === 'hidden' &&
        this.currentBannerAdId === options.adId
      ) {
        this.logBanner('show-decision-resume-bottom', { source, adId: options.adId });
        await this.resumeBanner(source);
        return;
      }

      if (
        placement === 'bottom' &&
        (this.bottomBannerState === 'visible' || this.bottomBannerState === 'showing') &&
        this.currentBannerAdId === options.adId
      ) {
        this.logBanner('show-skipped-active-bottom', { source, adId: options.adId });
        this.trackAdOutcome('banner_show_skipped_active_bottom', {
          source,
          placement,
          adId: options.adId,
          bottomBannerState: this.bottomBannerState,
          activeBannerPlacement: this.activeBannerPlacement,
        });
        return;
      }

      this.resizedAd.next(false);
      this.activeBannerPlacement = placement;
      this.currentBannerAdId = options.adId;
      if (placement === 'bottom') {
        this.clearPostTravelingFooterRestoreTimer();
        this.lastTravelingBannerRemovalAt = null;
        this.bottomBannerState = 'showing';
      }
      if (placement !== 'bottom') {
        this.clearBottomBannerOffset();
      }

      if (!this.isAdmin && !this.isPremium) {
        const bannerOptions: BannerAdOptions = { ...options };
        this.startBannerShowWatchdog(placement, source);
        this.logBanner('native-show-banner-request', {
          source,
          placement,
          adId: options.adId,
          adSize: options.adSize,
          position: options.position,
          isTesting: options.isTesting,
        });
        await this.adMob.showBanner(bannerOptions).catch(e => {
          this.logBanner('show-failed', {
            source,
            placement,
            adId: options.adId,
            adSize: options.adSize,
            position: options.position,
            error: this.sanitizeAdError(e),
          });
          this.trackAdOutcome('banner_show_failed', {
            source,
            placement,
            adId: options.adId,
            adSize: options.adSize,
            position: options.position,
            error: this.sanitizeAdError(e),
          });
          console.error(e);
          this.clearActiveBanner('removed');
        });
      }
    }
  }

  private logBanner(event: string, extra: Record<string, unknown> = {}) {
    if (environment.production && !this.enableProductionAdDiagnostics) {
      return;
    }
    this.syncPremiumState();

    const payload = {
      event,
      path: this.location.path(),
      isMobile: this.isMobile,
      isMobileWeb: this.isMobileWeb,
      isPremium: this.isPremium,
      isAdmin: this.isAdmin,
      userServiceIsPremium: !!this.userService.isPremium,
      userServiceIsAdmin: !!this.userService.isAdmin,
      userServiceIsPaidPremium: !!this.userService.isPaidPremium,
      bannerLoaded: this.bannerLoaded,
      appMargin: this.appMargin,
      activeBannerPlacement: this.activeBannerPlacement,
      bottomBannerState: this.bottomBannerState,
      currentBannerAdId: this.currentBannerAdId,
      listenerCount: this.listenerHandlers.length,
      ...extra,
    };

    console.log('[admob-lifecycle-json]', this.safeJsonStringify(payload));
  }

  private async showInterstitialAd(
    options: AdOptions,
    source = 'interstitial'
  ): Promise<boolean> {
    await this.refreshPremiumState();
    const adGateReady = this.isAdGateReady();
    if (!this.isMobile || !adGateReady || this.isPremium || this.isAdmin) {
      this.trackAdOutcome('interstitial_skipped_gate', {
        source,
        adId: options.adId,
        gateReason: this.adGateReason(
          !!this.userService.currentUser$.value?.uid,
          adGateReady
        ),
      });
      return false;
    }

    if (this.isMobile && !this.isPremium && !this.isAdmin) {
      const interstitialOptions: BannerAdOptions = { ...options };

      this.trackAdOutcome('interstitial_prepare_requested', {
        source,
        adId: options.adId,
        isTesting: options.isTesting,
      });
      this.logBanner('native-interstitial-prepare-request', {
        source,
        adId: options.adId,
        isTesting: options.isTesting,
      });

      const result1 = await this.adMob.prepareInterstitial(interstitialOptions).catch(e => {
        this.logBanner('interstitial-prepare-failed', { source, error: e });
        this.trackAdOutcome('interstitial_prepare_failed', {
          source,
          adId: options.adId,
          error: this.sanitizeAdError(e),
        });
        console.error(e);
        return undefined;
      });

      if (result1 === undefined) {
        return false;
      }

      this.trackAdOutcome('interstitial_show_requested', {
        source,
        adId: options.adId,
      });
      this.logBanner('native-interstitial-show-request', {
        source,
        adId: options.adId,
      });

      let didShowInterstitial = true;
      await this.adMob.showInterstitial().catch(e => {
        didShowInterstitial = false;
        this.logBanner('interstitial-show-failed', { source, error: e });
        this.trackAdOutcome('interstitial_show_failed', {
          source,
          adId: options.adId,
          error: this.sanitizeAdError(e),
        });
        console.error(e);
      });

      if (didShowInterstitial) {
        this.trackAdOutcome('interstitial_showed', {
          source,
          adId: options.adId,
        });
      }

      return didShowInterstitial;
    }

    return false;
  }

  private async showRewardAd(
    options: RewardAdOptions,
    source = 'reward'
  ): Promise<RewardAttemptOutcome> {
    await this.refreshPremiumState();
    const adGateReady = this.isAdGateReady();
    if (!this.isMobile || !adGateReady || this.isPremium || this.isAdmin) {
      this.trackAdOutcome('reward_skipped_gate', {
        source,
        adId: options.adId,
        gateReason: this.adGateReason(
          !!this.userService.currentUser$.value?.uid,
          adGateReady
        ),
      });
      return 'failed';
    }

    const rewardOptions: RewardAdOptions = { ...options };

    this.trackAdOutcome('reward_prepare_requested', {
      source,
      adId: options.adId,
      isTesting: options.isTesting,
    });
    this.logBanner('native-reward-prepare-request', {
      source,
      adId: options.adId,
      isTesting: options.isTesting,
    });

    const result = await this.adMob.prepareRewardVideoAd(rewardOptions).catch(e => {
      this.logBanner('reward-prepare-failed', { source, error: e });
      this.trackAdOutcome('reward_prepare_failed', {
        source,
        adId: options.adId,
        error: this.sanitizeAdError(e),
      });
      console.error(e);
      return undefined;
    });

    if (result === undefined) {
      return 'failed';
    }

    this.trackAdOutcome('reward_show_requested', {
      source,
      adId: options.adId,
    });
    this.logBanner('native-reward-show-request', {
      source,
      adId: options.adId,
    });

    const result2 = await this.adMob.showRewardVideoAd().catch(e => {
      this.logBanner('reward-show-failed', { source, error: e });
      this.trackAdOutcome('reward_show_failed', {
        source,
        adId: options.adId,
        error: this.sanitizeAdError(e),
      });
      console.error(e);
      return undefined;
    });

    return result2 === undefined ? 'failed' : 'shown';
  }
  async refreshPremiumState() {
    if (!this.userService.currentUser$.value?.uid) {
      await firstValueFrom(
        this.userService.currentUser$.pipe(
          filter(user => !!user?.uid),
          take(1)
        )
      );
    }

    this.syncPremiumState();
  }

  private syncPremiumState() {
    this.isPremium = !!this.userService.isPremium;
    this.isAdmin = !!this.userService.isAdmin;
  }

  private getAdGateReadyStream() {
    return this.userService.adGateReady$ || new BehaviorSubject<boolean>(true);
  }

  private isAdGateReady(): boolean {
    return this.userService.adGateReady$?.value ?? true;
  }

  private adGateReason(
    hasUser = !!this.userService.currentUser$.value?.uid,
    adGateReady = this.isAdGateReady()
  ): string {
    if (!this.isMobile) {
      return 'not-mobile';
    }
    if (!hasUser) {
      return 'no-authenticated-user';
    }
    if (!adGateReady) {
      return 'ad-gate-waiting';
    }
    if (this.isAdmin) {
      return 'admin';
    }
    if (this.isPremium) {
      return 'premium';
    }
    return 'eligible';
  }

  private registerListeners() {
    if (this.listenersRegistered) {
      return;
    }

    this.resizedAd.next(false);
    const resizeHandler = this.adMob.addListener(
      BannerAdPluginEvents.SizeChanged,
      (info: AdMobBannerSize) => this.handleBannerSizeChanged(info)
    );

    const bannerLoadedHandler = this.adMob.addListener(BannerAdPluginEvents.Loaded, () => {
      this.handleBannerLoaded('loaded');
    });

    const bannerFailedHandler = this.adMob.addListener(
      BannerAdPluginEvents.FailedToLoad,
      error => this.handleBannerFailedToLoad(error)
    );

    const bannerImpressionHandler = this.adMob.addListener(
      BannerAdPluginEvents.AdImpression,
      () => this.handleBannerLoaded('impression')
    );

    const adLoaded = this.adMob.addListener(BannerAdPluginEvents.Opened, () => {
      this.handleBannerLoaded('opened');
      this.logBanner('opened');
    });

    const rewardHandler = this.adMob.addListener(
      RewardAdPluginEvents.Loaded,
      (info: AdLoadInfo) => {
        this.trackAdOutcome('reward_loaded', { info });
        // Subscribe prepared rewardVideo
      }
    );

    const rewardShowedHandler = this.adMob.addListener(RewardAdPluginEvents.Showed, () => {
      this.trackAdOutcome('reward_showed_event');
    });

    const rewardFailedToLoadHandler = this.adMob.addListener(
      RewardAdPluginEvents.FailedToLoad,
      error => this.trackAdOutcome('reward_failed_to_load_event', {
        error: this.sanitizeAdError(error),
      })
    );

    const rewardFailedToShowHandler = this.adMob.addListener(
      RewardAdPluginEvents.FailedToShow,
      error => {
        this.trackAdOutcome('reward_failed_to_show_event', {
          error: this.sanitizeAdError(error),
        });
      }
    );

    const rewardDismissedHandler = this.adMob.addListener(
      RewardAdPluginEvents.Dismissed,
      () => {
        this.trackAdOutcome('reward_dismissed_event');
      }
    );

    const rewardUserHandler = this.adMob.addListener(
      RewardAdPluginEvents.Rewarded,
      (rewardItem: AdMobRewardItem) => {
        this.isRewarded.next(true);
        this.trackAdOutcome('reward_granted', { rewardItem });
      }
    );

    const interstitialFailedToLoadHandler = this.adMob.addListener(
      InterstitialAdPluginEvents.FailedToLoad,
      error => this.trackAdOutcome('interstitial_failed_to_load_event', {
        error: this.sanitizeAdError(error),
      })
    );

    const interstitialLoadedHandler = this.adMob.addListener(
      InterstitialAdPluginEvents.Loaded,
      (info: AdLoadInfo) => this.trackAdOutcome('interstitial_loaded_event', { info })
    );

    const interstitialShowedHandler = this.adMob.addListener(
      InterstitialAdPluginEvents.Showed,
      () => this.trackAdOutcome('interstitial_showed_event')
    );

    const interstitialFailedToShowHandler = this.adMob.addListener(
      InterstitialAdPluginEvents.FailedToShow,
      error => {
        this.trackAdOutcome('interstitial_failed_to_show_event', {
          error: this.sanitizeAdError(error),
        });
      }
    );

    const interstitialDismissedHandler = this.adMob.addListener(
      InterstitialAdPluginEvents.Dismissed,
      () => {
        this.trackAdOutcome('interstitial_dismissed_event');
      }
    );

    this.listenerHandlers.push(rewardHandler);
    this.listenerHandlers.push(rewardShowedHandler);
    this.listenerHandlers.push(rewardFailedToLoadHandler);
    this.listenerHandlers.push(rewardFailedToShowHandler);
    this.listenerHandlers.push(rewardDismissedHandler);
    this.listenerHandlers.push(rewardUserHandler);
    this.listenerHandlers.push(interstitialFailedToLoadHandler);
    this.listenerHandlers.push(interstitialLoadedHandler);
    this.listenerHandlers.push(interstitialShowedHandler);
    this.listenerHandlers.push(interstitialFailedToShowHandler);
    this.listenerHandlers.push(interstitialDismissedHandler);
    this.listenerHandlers.push(adLoaded);
    this.listenerHandlers.push(bannerLoadedHandler);
    this.listenerHandlers.push(bannerFailedHandler);
    this.listenerHandlers.push(bannerImpressionHandler);
    this.listenerHandlers.push(resizeHandler);
    this.listenersRegistered = true;
    this.logBanner('listeners-registered', { listenerCount: this.listenerHandlers.length });
  }

  private removeListeners() {
    this.listenerHandlers.forEach(handler => handler.remove());
    this.listenerHandlers.length = 0;
    this.listenersRegistered = false;
    this.logBanner('listeners-removed');
  }

  private handleBannerLoaded(
    nativeEvent: 'loaded' | 'opened' | 'impression',
    info: unknown = {}
  ) {
    this.clearBannerShowWatchdog();
    this.bannerLoaded = true;
    if (this.activeBannerPlacement === 'bottom') {
      this.bottomBannerState = 'visible';
    }
    this.trackAdOutcome('banner_loaded', {
      nativeEvent,
      placement: this.activeBannerPlacement,
      adId: this.currentBannerAdId,
      info,
    });
  }

  private handleBannerFailedToLoad(error: unknown) {
    const failedPlacement = this.activeBannerPlacement;
    const failedAdId = this.currentBannerAdId;
    const sanitizedError = this.sanitizeAdError(error);
    this.logBanner('banner-failed-to-load', {
      placement: failedPlacement,
      adId: failedAdId,
      expectedFooterAdId: bannerBottomOptions.adId,
      expectedFooterAdSize: bannerBottomOptions.adSize,
      expectedFooterPosition: bannerBottomOptions.position,
      footerCooldownRemainingMs: this.getFooterCooldownRemainingMs(),
      error: sanitizedError,
    });
    this.trackAdOutcome('banner_failed_to_load', {
      placement: failedPlacement,
      adId: failedAdId,
      expectedFooterAdId: bannerBottomOptions.adId,
      expectedFooterAdSize: bannerBottomOptions.adSize,
      expectedFooterPosition: bannerBottomOptions.position,
      footerCooldownRemainingMs: this.getFooterCooldownRemainingMs(),
      error: sanitizedError,
    });

    if (failedPlacement === 'bottom') {
      if (failedAdId === bannerBottomOptions.adId) {
        this.startFooterBannerCooldown(sanitizedError);
      }
      this.clearActiveBanner('removed');
      return;
    }

    if (failedPlacement === 'traveling') {
      this.logBanner('mrec-no-fill', {
        adId: failedAdId,
        error: sanitizedError,
        footerCooldownRemainingMs: this.getFooterCooldownRemainingMs(),
      });
      this.trackAdOutcome('mrec_no_fill', {
        placement: failedPlacement,
        adId: failedAdId,
        error: sanitizedError,
      });
    }

    this.clearActiveBanner('none');
  }

  private shouldSkipFooterRequestForCooldown(adId: string | undefined, source: string): boolean {
    if (adId !== bannerBottomOptions.adId || !this.footerBannerCooldownUntil) {
      return false;
    }

    const remainingMs = this.footerBannerCooldownUntil - Date.now();
    if (remainingMs <= 0) {
      this.logBanner('footer-cooldown-expired', {
        source,
        adId,
        previousFailure: this.footerBannerLastFailureReason,
      });
      this.footerBannerCooldownUntil = null;
      this.footerBannerLastFailureReason = null;
      return false;
    }

    this.logBanner('footer-skipped-cooldown', {
      source,
      adId,
      remainingMs,
      cooldownUntil: this.footerBannerCooldownUntil,
      previousFailure: this.footerBannerLastFailureReason,
    });
    this.trackAdOutcome('banner_show_skipped_footer_cooldown', {
      source,
      placement: 'bottom',
      adId,
      remainingMs,
      cooldownUntil: this.footerBannerCooldownUntil,
      previousFailure: this.footerBannerLastFailureReason,
    });
    return true;
  }

  private shouldSkipFooterRequestAfterTraveling(source: string): boolean {
    if (!this.lastTravelingBannerRemovalAt) {
      return false;
    }

    const remainingMs =
      this.lastTravelingBannerRemovalAt +
      this.postTravelingFooterRestoreDelayMs -
      Date.now();

    if (remainingMs <= 0) {
      this.lastTravelingBannerRemovalAt = null;
      return false;
    }

    this.logBanner('show-skipped-post-mrec-delay', {
      source,
      remainingMs,
      lastTravelingBannerRemovalAt: this.lastTravelingBannerRemovalAt,
    });
    this.trackAdOutcome('banner_show_skipped_post_mrec_delay', {
      source,
      placement: 'bottom',
      adId: bannerBottomOptions.adId,
      remainingMs,
      lastTravelingBannerRemovalAt: this.lastTravelingBannerRemovalAt,
    });
    this.schedulePostTravelingFooterRestoreRetry(remainingMs, source);
    return true;
  }

  private schedulePostTravelingFooterRestoreRetry(remainingMs: number, source: string) {
    if (this.postTravelingFooterRestoreTimer) {
      this.logBanner('post-mrec-footer-retry-already-scheduled', {
        source,
        remainingMs,
      });
      return;
    }

    const retryDelayMs = Math.max(0, remainingMs) + 50;
    this.logBanner('post-mrec-footer-retry-scheduled', {
      source,
      retryDelayMs,
    });
    this.trackAdOutcome('banner_post_mrec_restore_retry_scheduled', {
      source,
      placement: 'bottom',
      adId: bannerBottomOptions.adId,
      retryDelayMs,
    });

    this.postTravelingFooterRestoreTimer = setTimeout(() => {
      this.postTravelingFooterRestoreTimer = null;
      this.logBanner('post-mrec-footer-retry-fired', { source });
      this.queueCurrentFooterBannerDecision(`post-mrec-delay-expired:${source}`);
    }, retryDelayMs);
  }

  private clearPostTravelingFooterRestoreTimer() {
    if (!this.postTravelingFooterRestoreTimer) {
      return;
    }

    clearTimeout(this.postTravelingFooterRestoreTimer);
    this.postTravelingFooterRestoreTimer = null;
  }

  private startFooterBannerCooldown(error: SanitizedAdError) {
    this.footerBannerCooldownUntil = Date.now() + this.footerBannerCooldownMs;
    this.footerBannerLastFailureReason = error;
    this.logBanner('footer-cooldown-started', {
      cooldownUntil: this.footerBannerCooldownUntil,
      cooldownMs: this.footerBannerCooldownMs,
      error,
    });
    this.trackAdOutcome('banner_footer_cooldown_started', {
      placement: 'bottom',
      adId: bannerBottomOptions.adId,
      cooldownUntil: this.footerBannerCooldownUntil,
      cooldownMs: this.footerBannerCooldownMs,
      error,
    });
  }

  private getFooterCooldownRemainingMs(): number {
    return this.footerBannerCooldownUntil
      ? Math.max(0, this.footerBannerCooldownUntil - Date.now())
      : 0;
  }

  private startBannerShowWatchdog(placement: BannerPlacement, source: string) {
    this.clearBannerShowWatchdog();
    this.bannerShowWatchdogTimer = setTimeout(() => {
      if (placement !== this.activeBannerPlacement) {
        return;
      }

      if (placement === 'bottom' && this.bottomBannerState !== 'showing') {
        return;
      }

      this.logBanner('banner-show-watchdog-expired', { source, placement });
      this.trackAdOutcome('banner_show_watchdog_expired', {
        source,
        placement,
        adId: this.currentBannerAdId,
      });
      this.handleBannerFailedToLoad({ message: 'Banner did not report native load before watchdog timeout' });
    }, this.bannerShowWatchdogMs);
  }

  private clearBannerShowWatchdog() {
    if (!this.bannerShowWatchdogTimer) {
      return;
    }

    clearTimeout(this.bannerShowWatchdogTimer);
    this.bannerShowWatchdogTimer = null;
  }

  private handleBannerSizeChanged(info: AdMobBannerSize) {
    this.logBanner('size-changed', {
      height: info.height,
      width: info.width,
      placement: this.activeBannerPlacement,
    });

    const app: HTMLElement = document.querySelector('ion-router-outlet');
    this.resetRouterOutletMargin(app);

    if (this.activeBannerPlacement === 'bottom' && info.height > 0) {
      this.clearBannerShowWatchdog();
      this.appMargin = info.height;
      this.resizedAd.next(true);
      this.bottomBannerState = 'visible';
      this.applyBottomBannerOffset(info.height);
      return;
    }

    this.resizedAd.next(false);
    this.clearBottomBannerOffset();
  }

  private applyBottomBannerOffset(height: number) {
    document.body.classList.add(this.bottomBannerClass);
    document.body.style.setProperty(this.bottomBannerHeightVar, `${height}px`);
  }

  private clearActiveBanner(bottomState: BottomBannerState = 'none') {
    this.clearBannerShowWatchdog();
    if (bottomState !== 'removed') {
      this.clearPostTravelingFooterRestoreTimer();
    }
    this.activeBannerPlacement = null;
    this.currentBannerAdId = null;
    this.bannerLoaded = false;
    this.bottomBannerState = bottomState;
    this.appMargin = 0;
    this.resizedAd.next(false);
    this.clearBottomBannerOffset();
  }

  private markBottomBannerHidden() {
    this.clearBannerShowWatchdog();
    this.bannerLoaded = false;
    this.bottomBannerState = 'hidden';
    this.appMargin = 0;
    this.resizedAd.next(false);
    this.clearBottomBannerOffset();
  }

  private clearBottomBannerOffset() {
    document.body.classList.remove(this.bottomBannerClass);
    document.body.style.removeProperty(this.bottomBannerHeightVar);
  }

  private resetRouterOutletMargin(app: HTMLElement) {
    if (!app) {
      return;
    }

    app.style.marginTop = '';
    app.style.marginBottom = '0px';
  }

  private sanitizeAdError(error: unknown): SanitizedAdError {
    if (!error || typeof error !== 'object') {
      return { message: String(error || '') };
    }

    const maybeError = error as Record<string, unknown>;

    return {
      code: maybeError.code,
      domain: maybeError.domain,
      name: maybeError.name,
      message: maybeError.message,
      responseInfo: maybeError.responseInfo,
      status: maybeError.status,
    };
  }

  private trackAdOutcome(event: string, extra: Record<string, unknown> = {}) {
    const payload = {
      event,
      path: this.location.path(),
      isTesting:
        extra['isTesting'] ??
        (extra['adId'] === bannerBottomOptions.adId
          ? bannerBottomOptions.isTesting
          : undefined),
      isMobile: this.isMobile,
      isPremium: this.isPremium,
      isAdmin: this.isAdmin,
      environmentProduction: environment.production,
      ...extra,
    };

    if (!environment.production || this.enableProductionAdDiagnostics) {
      console.log('[admob-outcome-json]', this.safeJsonStringify(payload));
    }

    try {
      mixpanel.track('AdMob Outcome', payload);
    } catch {
      // Ad diagnostics must never affect user flows.
    }
  }

  private safeJsonStringify(payload: Record<string, unknown>): string {
    const seen = new WeakSet<object>();
    try {
      return JSON.stringify(payload, (_key, value) => {
        if (value instanceof Error) {
          return {
            name: value.name,
            message: value.message,
            stack: value.stack,
          };
        }
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            return '[circular]';
          }
          seen.add(value);
        }
        if (typeof value === 'function') {
          return '[function]';
        }
        return value;
      });
    } catch (error) {
      const fallback = {
        event: payload['event'],
        path: payload['path'],
        stringifyError: this.sanitizeAdError(error),
      };
      return JSON.stringify(fallback);
    }
  }
}
